"use client";

import {
  SiteNavbar,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@pex/ui-web";
import { Check, LocateFixed, Minus, Plus } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ProgrammeTypeOption,
  UrlFilters,
} from "@/components/in-person/filter-bar";
import { FilterBar } from "@/components/in-person/filter-bar";
import type { DiscoveryItem, FilterOptions } from "@/lib/queries/discovery";
import type { SearchSuggestion } from "@/lib/search";
import {
  GROUP_TYPE_OPTIONS,
  matchesGroupType,
  SPECIALISATION_LABELS,
} from "@/lib/specialisation-labels";
import type { MapHandle } from "./map-with-pins";
import { MapWithPins } from "./map-with-pins";

interface Props {
  avatarUrl?: string | null;
  filterOptions: FilterOptions;
  initialFeed: DiscoveryItem[];
}

const VALID_TYPES = ["all", "group", "programme"] as const;

function parseEnum<T extends string>(
  value: string | null,
  valid: readonly T[],
  fallback: T
): T {
  return valid.includes(value as T) ? (value as T) : fallback;
}

function fromParams(p: URLSearchParams): UrlFilters {
  return {
    group_type: p.get("grp") ?? "",
    meeting_day: p.get("day") ?? "",
    programme_type: p.get("prog") ?? "",
    type: parseEnum(p.get("type"), VALID_TYPES, "all"),
  };
}

function toParams(f: UrlFilters): string {
  const p = new URLSearchParams();
  if (f.type !== "all") {
    p.set("type", f.type);
  }
  if (f.meeting_day) {
    p.set("day", f.meeting_day);
  }
  if (f.programme_type) {
    p.set("prog", f.programme_type);
  }
  if (f.group_type) {
    p.set("grp", f.group_type);
  }
  return p.toString();
}

function matchesSearch(item: DiscoveryItem, query: string): boolean {
  const q = query.toLowerCase();
  const name =
    item.kind === "group" ? item.data.name : (item.data.definition?.name ?? "");

  if (name.toLowerCase().includes(q)) {
    return true;
  }
  if ((item.data.city ?? "").toLowerCase().includes(q)) {
    return true;
  }
  if ((item.data.suburb ?? "").toLowerCase().includes(q)) {
    return true;
  }
  if ((item.data.postal_code ?? "").toLowerCase().includes(q)) {
    return true;
  }

  if (item.kind === "group") {
    if ((item.data.country ?? "").toLowerCase().includes(q)) {
      return true;
    }
    // Allow searching by specialisation label words (e.g. "supporter")
    const specLabel = (item.data.specialisation ?? "").replace(/_/g, " ");
    if (specLabel.toLowerCase().includes(q)) {
      return true;
    }
  }

  return false;
}

function applyFilters(
  items: DiscoveryItem[],
  filters: UrlFilters,
  search: string
): DiscoveryItem[] {
  return items.filter((item) => {
    if (item.data.format !== "in_person") {
      return false;
    }
    if (filters.type !== "all" && item.kind !== filters.type) {
      return false;
    }
    if (search && !matchesSearch(item, search)) {
      return false;
    }
    if (
      filters.meeting_day &&
      item.data.meeting_day?.toLowerCase() !== filters.meeting_day
    ) {
      return false;
    }
    if (
      filters.programme_type &&
      item.kind === "programme" &&
      item.data.definition?.code !== filters.programme_type
    ) {
      return false;
    }
    if (
      filters.group_type &&
      item.kind === "group" &&
      !matchesGroupType(item.data.specialisation, filters.group_type)
    ) {
      return false;
    }
    return true;
  });
}

export function InPersonScreen({
  avatarUrl,
  filterOptions,
  initialFeed,
}: Props) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const filters = useMemo(() => fromParams(searchParams), [searchParams]);
  const [searchText, setSearchText] = useState("");

  const mapHandleRef = useRef<MapHandle | null>(null);
  const [noGroupsInArea, setNoGroupsInArea] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(false);
  const [locateSuccess, setLocateSuccess] = useState(false);

  const navigate = useCallback(
    (updates: Partial<UrlFilters>) => {
      const next = { ...filters, ...updates };
      const qs = toParams(next);
      router.replace(qs ? `?${qs}` : "?", { scroll: false });
    },
    [filters, router]
  );

  // Derive distinct programme definitions from the full feed (not filtered),
  // so options remain stable as other filters change.
  const programmeTypes = useMemo((): ProgrammeTypeOption[] => {
    const seen = new Map<string, ProgrammeTypeOption>();
    for (const item of initialFeed) {
      if (
        item.kind === "programme" &&
        item.data.format === "in_person" &&
        item.data.definition
      ) {
        const { code, description, name, logo_letter_url } =
          item.data.definition;
        if (!seen.has(code)) {
          seen.set(code, { code, description, logoUrl: logo_letter_url, name });
        }
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [initialFeed]);

  // Only show group type filter options that have at least one match in the feed,
  // preserving the canonical display order from GROUP_TYPE_OPTIONS.
  const groupTypeOptions = useMemo(() => {
    const present = new Set<string>();
    for (const item of initialFeed) {
      if (
        item.kind === "group" &&
        item.data.format === "in_person" &&
        item.data.specialisation
      ) {
        present.add(item.data.specialisation);
      }
    }
    return GROUP_TYPE_OPTIONS.filter((o) =>
      o.matches.some((m) => present.has(m))
    );
  }, [initialFeed]);

  // Items filtered by URL params only — these drive the map pins.
  // Decoupled from searchText so typing in the search box doesn't rebuild markers.
  const urlFilteredItems = useMemo(
    () => applyFilters(initialFeed, filters, ""),
    [initialFeed, filters]
  );

  // Items also filtered by text search — used as the client-side augmentation
  // universe for the search dropdown (not passed to the map).
  const textFilteredItems = useMemo(
    () =>
      searchText
        ? applyFilters(initialFeed, filters, searchText)
        : urlFilteredItems,
    [initialFeed, filters, searchText, urlFilteredItems]
  );

  // Dismiss "no groups" banner whenever filtered items change (e.g. filter pill used)
  // biome-ignore lint/correctness/useExhaustiveDependencies: urlFilteredItems is an intentional trigger — the effect body only needs to reset state
  useEffect(() => {
    setNoGroupsInArea(false);
  }, [urlFilteredItems]);

  // Type-aware "no groups" label based on active filter pill
  let noGroupsLabel: string;
  if (filters.group_type) {
    noGroupsLabel = `No ${SPECIALISATION_LABELS[filters.group_type] ?? "groups"} groups in this area`;
  } else if (filters.programme_type) {
    noGroupsLabel = `No ${filters.programme_type.toUpperCase()} programmes in this area`;
  } else {
    noGroupsLabel = "No groups in this area";
  }

  const handleSuggestionSelect = useCallback(
    (suggestion: SearchSuggestion) => {
      setNoGroupsInArea(false);

      if (suggestion.kind === "place") {
        if (suggestion.bbox) {
          // Region/country — fit the full bounding box so all groups within it are visible
          mapHandleRef.current?.fitBounds(suggestion.bbox, () => {
            const bounds = mapHandleRef.current?.getBounds();
            if (!bounds) {
              return;
            }
            const anyVisible = urlFilteredItems.some(
              (item) =>
                item.data.latitude != null &&
                item.data.longitude != null &&
                bounds.contains([
                  item.data.longitude as number,
                  item.data.latitude as number,
                ])
            );
            setNoGroupsInArea(!anyVisible);
          });
        } else {
          // Point result (city centroid) — fly to zoom 12
          mapHandleRef.current?.flyTo(suggestion.coordinates, 12, () => {
            const bounds = mapHandleRef.current?.getBounds();
            if (!bounds) {
              return;
            }
            const anyVisible = urlFilteredItems.some(
              (item) =>
                item.data.latitude != null &&
                item.data.longitude != null &&
                bounds.contains([
                  item.data.longitude as number,
                  item.data.latitude as number,
                ])
            );
            setNoGroupsInArea(!anyVisible);
          });
        }
      } else {
        // Look up the full DiscoveryItem to open its PinDetailCard popup.
        // Open the popup only after the flyTo animation finishes (moveend).
        const item = initialFeed.find(
          (i) => i.kind === suggestion.kind && i.data.id === suggestion.id
        );
        if (!item) {
          return;
        }
        mapHandleRef.current?.flyTo(suggestion.coordinates, 14, () => {
          mapHandleRef.current?.openPopup(item);
        });
      }
    },
    [urlFilteredItems, initialFeed]
  );

  // "Find My Location" — request GPS and fly to the result
  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }
    setLocating(true);
    setLocateError(false);
    setLocateSuccess(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setLocateSuccess(true);
        mapHandleRef.current?.flyTo(
          [pos.coords.longitude, pos.coords.latitude],
          13
        );
        setTimeout(() => setLocateSuccess(false), 2000);
      },
      () => {
        setLocating(false);
        setLocateError(true);
      },
      { timeout: 10_000 }
    );
  }, []);

  // Expose current map center to the filter bar for Mapbox proximity bias.
  // Updated lazily via a stable callback; the FilterBar reads it on each fetch.
  const getMapCenter = useCallback(() => mapHandleRef.current?.getCenter(), []);

  return (
    <div className="relative h-dvh overflow-hidden">
      {/* Fixed top navbar */}
      <SiteNavbar
        avatarUrl={avatarUrl}
        currentApp="finder"
        fixed
        websiteUrl={process.env.NEXT_PUBLIC_WEBSITE_URL}
      />

      {/* Map — fills the viewport below the navbar */}
      <div className="relative h-full pt-16">
        <MapWithPins
          filtersActive={
            filters.type !== "all" ||
            !!filters.group_type ||
            !!filters.programme_type
          }
          items={urlFilteredItems}
          onUserInteraction={() => setNoGroupsInArea(false)}
          ref={mapHandleRef}
        />

        {/* Bottom action buttons — side-by-side on mobile, centred pills on desktop.
            Mobile bottom pad hugs the iOS home indicator via safe-area inset. */}
        <div className="absolute right-0 bottom-0 left-0 z-20 flex flex-row gap-2 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:right-auto sm:bottom-8 sm:left-1/2 sm:w-auto sm:-translate-x-1/2 sm:px-0 sm:pb-0">
          <TooltipProvider>
            <Tooltip open={locateError || undefined}>
              <TooltipTrigger asChild>
                <button
                  className={`inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full border font-medium text-sm shadow-md transition-colors disabled:opacity-60 sm:h-9 sm:w-auto sm:flex-none sm:justify-start sm:px-4 ${locateSuccess ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100" : "border-input bg-background hover:bg-accent"}`}
                  disabled={locating}
                  onClick={handleLocateMe}
                  type="button"
                >
                  {locating && <Spinner className="h-4 w-4" />}
                  {!locating && locateSuccess && <Check className="h-4 w-4" />}
                  {!(locating || locateSuccess) && (
                    <LocateFixed className="h-4 w-4" />
                  )}
                  <span>My location</span>
                </button>
              </TooltipTrigger>
              {locateError && (
                <TooltipContent>Location access denied</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Link
            className="inline-flex h-11 flex-1 items-center justify-center rounded-full border border-input bg-background font-medium text-sm shadow-md transition-colors hover:bg-accent sm:h-9 sm:w-auto sm:flex-none sm:px-4"
            href="/online"
          >
            Online Groups
          </Link>
        </div>

        {/* Zoom buttons — only shown at md+ where filter bar is single-row */}
        <div className="absolute top-20 right-4 z-20 hidden flex-col overflow-hidden rounded-lg border border-input bg-background shadow-md md:flex">
          <button
            aria-label="Zoom in"
            className="flex h-9 w-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
            onClick={() => mapHandleRef.current?.zoomIn()}
            type="button"
          >
            <Plus className="h-4 w-4" />
          </button>
          <div className="border-border border-t" />
          <button
            aria-label="Zoom out"
            className="flex h-9 w-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
            onClick={() => mapHandleRef.current?.zoomOut()}
            type="button"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>

        {/* No groups in area banner */}
        {noGroupsInArea && (
          <div className="pointer-events-none absolute bottom-20 left-1/2 z-20 -translate-x-1/2 rounded-full bg-background/90 px-4 py-2 text-muted-foreground text-sm shadow-md backdrop-blur-sm sm:bottom-24">
            {noGroupsLabel}
          </div>
        )}
      </div>

      {/* Floating filter bar — overlaid on the map */}
      <FilterBar
        filteredItems={textFilteredItems}
        filterOptions={filterOptions}
        filters={filters}
        getMapCenter={getMapCenter}
        groupTypeOptions={groupTypeOptions}
        onFiltersChange={navigate}
        onSearchChange={setSearchText}
        onSuggestionSelect={handleSuggestionSelect}
        programmeTypes={programmeTypes}
        searchText={searchText}
      />
    </div>
  );
}
